<template>
	<div>
		<div class="checker-upload">
			<span class="checker-upload__title">{{ label }}</span>
			<dropzone
				:options="uploaderOptions"
				useCustomSlot
				id="dropzone"
				ref="dropzone"
				@vdropzone-files-added="onFilesAdded"
				@vdropzone-sending="onFileSending"
				@vdropzone-upload-progress="onFileUploadProgress"
				@vdropzone-complete="onFileUploadComplete"
				@vdropzone-queue-complete="onQueueCompleted"
				@vdropzone-error="onFileUploadError"
			>
				<div :class="[{ error: inputState == 'error' }, 'checker-upload__zone']">
					<svg width="53" height="53" viewBox="0 0 53 53" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M44.167 16.272h.75a.75.75 0 00-.214-.524l-.536.524zM32.573 4.417l.536-.525a.75.75 0 00-.536-.225v.75zm-20.073 1v35.541H14V5.417h-1.5zm1.75 37.291h28.917v-1.5H14.25v1.5zm30.667-1.75V16.272h-1.5v24.686h1.5zm-.214-25.21L33.109 3.892l-1.072 1.049L43.63 16.796l1.073-1.048zM32.573 3.667H14.25v1.5h18.323v-1.5zm-.75.75v10.855h1.5V4.417h-1.5zm1.75 12.605h10.594v-1.5H33.573v1.5zm9.594 25.686a1.75 1.75 0 001.75-1.75h-1.5a.25.25 0 01-.25.25v1.5zM12.5 40.958c0 .967.784 1.75 1.75 1.75v-1.5a.25.25 0 01-.25-.25h-1.5zm19.323-25.686c0 .966.783 1.75 1.75 1.75v-1.5a.25.25 0 01-.25-.25h-1.5zM14 5.417a.25.25 0 01.25-.25v-1.5a1.75 1.75 0 00-1.75 1.75H14z" fill="#6D7989"/><path d="M5.875 12.042v35.541h1.5V12.042h-1.5zm1.75 37.291h28.917v-1.5H7.625v1.5zm5.625-39.041H7.625v1.5h5.625v-1.5zm25.042 37.291v-5.625h-1.5v5.625h1.5zm-1.75 1.75a1.75 1.75 0 001.75-1.75h-1.5a.25.25 0 01-.25.25v1.5zm-30.667-1.75c0 .967.784 1.75 1.75 1.75v-1.5a.25.25 0 01-.25-.25h-1.5zm1.5-35.541a.25.25 0 01.25-.25v-1.5a1.75 1.75 0 00-1.75 1.75h1.5z" fill="#6D7989"/><path d="M28.708 32.02V20.98m0 0l-4.416 4.52m4.416-4.52l4.417 4.52" stroke="#6D7989" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
					<div class="checker-upload__textContainer">
						<span class="checker-upload__text">
							Drag and drop or browse files
						</span>
						<span class="checker-upload__subtext">
							{{ subText }}
						</span>
					</div>
					<input
						class="checker-upload__input"
						type="file"
						multiple="multiple"
						accept="application/zip, multipart/x-zip, application/x-compressed, application/x-zip-compressed"
					/>
				</div>
			</dropzone>
			<span class="checker-upload__hint">
				{{ hint }}
			</span>
		</div>
		<checker-upload-list
			:files="uploadedFiles"
			@remove="onRemove"
			@cancel="onCancel"
		/>
		<div v-if="inputState == 'error'" class="errorMessage">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm1-14a1 1 0 10-2 0 1 1 0 002 0zm-1 9a1 1 0 001-1v-5a1 1 0 10-2 0v5a1 1 0 001 1z" fill="currentColor"/></svg>
            <span>{{ errMessage }}</span>
        </div>
	</div>
</template>

<script>
import dropzone from "vue2-dropzone";
import CheckerUploadList from "@/components/CheckerUploadList.vue";

let checkQueue = null;
let clearQueue = null;

export default {
	name: "CheckerUpload",
	components: {
		dropzone,
		CheckerUploadList,
	},
	props: {
		acceptedFiles: {
			type: String,
			default: "image/jpeg, image/png, application/pdf",
		},
		url: {
			type: String,
			required: true,
		},
		maxTotalFileSize: {
			type: [Number, String],
			default: 2,
		},
		headers: {
			type: Object,
			default: () => {
				return {};
			},
		},
		label: {
			type: String,
		},
		subText: {
			type: String,
		},
		hint: {
			type: String,
		},
		inputState: {
            type: String,
        },
        errMessage: {
            type: String,
        }
	},
	data() {
		return {
			uploadedFiles: [],
			uploadedFilesSize: 0,
			isUploading: false,
			hasError: false,
			key: 100,
		};
	},
	watch: {
		value() {
			this.uploadedFiles = this.value ? this.value.slice(0) : [];
			this.uploadedFilesSize = 0;
			if (this.uploadedFiles.length > 0) {
				this.uploadedFiles.forEach((file) => {
					this.uploadedFilesSize += Number(
						(file.size / (1024 * 1024)).toFixed(4)
					);
				});
			}
		},
		params() {
			this.key++;
		},
	},
	computed: {
		maxTotalFileSizeNumber() {
			return this.maxTotalFileSize &&
				Number(this.maxTotalFileSize) &&
				Number(this.maxTotalFileSize) > 0
				? Math.round(Number(this.maxTotalFileSize) * 100) / 100
				: 2;
		},
		uploaderOptions() {
			return {
				url: this.url,
				headers: this.headers,
				acceptedFiles: this.acceptedFiles,
				maxFilesize: 10,
				withCredentials: false,
				previewTemplate: "<div></div>",
				autoProcessQueue: false,
				parallelUploads: 10,
			};
		},
	},
	methods: {
		checkQueuedFiles(files) {
			files = files.filter((file) => file.status === "queued");
			if (this.$refs.dropzone.getQueuedFiles().length === files.length) {
				this.$refs.dropzone.processQueue();
				this.isUploading = true;
				clearInterval(checkQueue);
			}
		},

		clearQueuedFiles(files) {
			files = files.filter((file) => file.status === "queued");
			if (this.$refs.dropzone.getQueuedFiles().length === files.length) {
				files.forEach((file) => this.$refs.dropzone.removeFile(file));
				clearInterval(clearQueue);
			}
			this.hasError = false;
		},

		getAllFileName(files) {
			let fileNameArray = [];
			this.uploadedFiles.forEach((uploadedFile) =>
				fileNameArray.push(uploadedFile.name)
			);
			files.forEach((file) => fileNameArray.push(file.name));
			return fileNameArray;
		},

		getNonEmptyFiles(files) {
			let nonEmptyFiles = [];
			files.forEach((file) => {
				if (file.size && file.size > 0) {
					nonEmptyFiles.push(file);
				}
			});
			return nonEmptyFiles;
		},

		getValidFiles(files) {
			let validFiles = [];
			files.forEach((file) => {
				if (file.type && this.acceptedFiles.includes(file.type)) {
					validFiles.push(file);
				}
			});
			return validFiles;
		},

		checkDuplicateFileName(allFileName) {
			let allFileNameLowerCase = [];
			allFileName.forEach((fileName) =>
				allFileNameLowerCase.push(fileName.toLowerCase())
			);
			let allFileNameLowerCaseUnique = new Set(allFileNameLowerCase);
			return (
				allFileNameLowerCase.length !== allFileNameLowerCaseUnique.size
			);
		},

		getTotalFileSize(files) {
			let totalFileSize = 0;
			files.forEach(
				(file) =>
					(totalFileSize += Number(
						(file.size / (1024 * 1024)).toFixed(4)
					))
			);
			return totalFileSize;
		},

		onFilesAdded(files) {
			if (!Array.isArray(files)) {
				files = Object.keys(files).map(function(e) {
					return files[e];
				});
			}
			let nonEmptyFiles = this.getNonEmptyFiles(files);

			if (nonEmptyFiles.length === files.length) {
				let validFiles = this.getValidFiles(files);

				if (validFiles.length === files.length) {
					let addedFilesSize = this.getTotalFileSize(files);

					if (
						this.uploadedFilesSize + addedFilesSize <=
						this.maxTotalFileSizeNumber
					) {
						let allFileName = this.getAllFileName(files);
						let hasDuplicateFileName = this.checkDuplicateFileName(
							allFileName
						);

						if (!hasDuplicateFileName) {
							checkQueue = setInterval(
								() => this.checkQueuedFiles(files),
								100
							);
						} else {
							clearQueue = setInterval(
								() => this.clearQueuedFiles(files),
								100
							);
							this.$emit("error", "Duplicate files");
						}
					} else {
						clearQueue = setInterval(
							() => this.clearQueuedFiles(files),
							100
						);
						this.$emit("error", "Invalid file size");
					}
				} else {
					clearQueue = setInterval(
						() => this.clearQueuedFiles(files),
						100
					);
					this.$emit("error", "Invalid file format");
				}
			} else {
				clearQueue = setInterval(
					() => this.clearQueuedFiles(files),
					100
				);
				this.$emit("error", "Empty file");
			}
		},

		onFileSending(file, xhr, formData) {
			this.uploadedFiles.push({
				file: file,
				id: file.upload.uuid,
				name: file.name,
				size: file.size,
				type: file.type,
				progress: 0,
				uploadCompleted: false,
			});
			this.uploadedFilesSize += file.size / (1024 * 1024);
			formData.append("id", file.upload.uuid);
			formData.append("name", file.name);
			formData.append("size", file.size);
			formData.append("type", file.type);
		},

		onFileUploadProgress(uploadingFile, uploadingProgress) {
			let resultFile = this.uploadedFiles.find(
				(file) => file.id === uploadingFile.upload.uuid
			);
			resultFile.progress = Math.round(uploadingProgress);
		},

		onFileUploadComplete(response) {
			if (response.status === "success") {
				let resultFile = this.uploadedFiles.find(
					(file) => file.id === response.upload.uuid
				);
				resultFile.uploadCompleted = true;
				resultFile.response = response.xhr.response;
				/**
				 * When a file is uploaded
				 * @event file-uploaded
				 * @type {file}
				 */
				this.$emit("file-uploaded", resultFile);
			}
		},

		onQueueCompleted() {
			this.isUploading = false;
			if (!this.hasError) {
				/**
				 * When file list is updated
				 * @event input
				 * @type {array}
				 */
				this.$emit("input", this.uploadedFiles);
			}
			this.hasError = false;
		},

		onFileUploadError(file, error) {
			this.hasError = true;
			if (
				typeof error !== "string" ||
				(error !== "You can't upload files of this type." &&
					!error.includes("File is too big"))
			) {
				/**
				 * When error is trigger
				 * @event error
				 * @type {string}
				 */
				this.removeFile({ id: file.upload.uuid, size: file.size });
				this.$refs.dropzone.removeAllFiles(true);
				this.$emit("error", error);
			}
		},

		onRemove(file) {
			/**
			 * When a remove button is clicked
			 * @event remove
			 * @type {file}
			 */
			this.$emit("remove", file);
		},

		onCancel(file) {
			this.$refs.dropzone.removeFile(file.file);
			/**
			 * When a cancel button is clicked
			 * @event cancel
			 * @type {file}
			 */
			this.$emit("cancel", file);
		},

		/**
		 * To remove a file
		 *
		 * @param {removedFile}
		 * @public
		 */
		removeFile(removedFile) {
			this.uploadedFiles = this.uploadedFiles.filter(
				(file) => file.id !== removedFile.id
			);
			this.uploadedFilesSize -= removedFile.size / (1024 * 1024);
			/**
			 * When a file is removed
			 * @event file-removed
			 * @type {file}
			 */
			this.$emit("file-removed", removedFile);
			this.$emit("input", this.uploadedFiles);
		},
	},
};
</script>

<style lang="scss" scoped>
.checker-upload {
	display: flex;
	flex-direction: column;
	gap: rem(8);
}

.checker-upload__zone {
	border: 2px dashed $color-cool-grey-20;
	border-radius: 4px;
	background: $color-white;
	display: flex;
	flex-direction: row;
	gap: rem(18);
	padding: rem(25) rem(62);
	width: rem(430);
    justify-content: center;
	position: relative;
	cursor: pointer;

	&:hover {
		border-color: $color-blue-50;
		background: $color-blue-00;
	}
}

.checker-upload__input {
	visibility: hidden;
	position: absolute;
	top: 0px;
	left: 0px;
	height: 0px;
	width: 0px;
}

.checker-upload__textContainer {
	display: flex;
	flex-direction: column;
	gap: rem(4);
}

.checker-upload__subtext,
.checker-upload__hint {
	@include font-size(s);
	color: $color-cool-grey-70;
}

.error {
	border-color: $color-red-80;
}

.errorMessage {
	display: flex;
	color: $color-red-80;
	gap: rem(5);
	
	span {
		color: $color-red-80;
	}
}
</style>
